import React, { useState, useEffect, useCallback, Fragment } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { Dialog, Transition } from "@headlessui/react";
import Layout from "../../components/global/layout";
import ApiClient from "../../methods/api/apiClient";
import loader from "../../methods/loader";
import swal from "sweetalert";
import { toast } from "react-toastify";
import ActionMenu from "../../components/common/ActionMenu";
import CommonPagination from "../../components/common/CommonPagination";
import FormControl from "../../components/common/FormControl";
import { MODULE_CONFIG } from "./shared";
import {
  capitalize,
  capitalizeSentence,
  formatDate,
  formatTime,
  getInitials,
} from "../../methods/methods";
import {
  FiMail,
  FiSearch,
  FiEye,
  FiCheckCircle,
  FiClock,
  FiTrash2,
  FiCornerUpLeft,
  FiX,
  FiSend,
  FiRotateCcw,
} from "react-icons/fi";
import { HiOutlineBuildingOffice2 } from "react-icons/hi2";
import { hasPermission } from "../../utils/permission";

export default function ContactUsList() {
  const navigate = useNavigate();
  const user = useSelector((state) => state.user);
  const logedInUser = JSON.parse(
    sessionStorage.getItem("logedInUser") || JSON.stringify(user || {})
  );

  // Dynamic permission check for staff / sub-admins
  const isSuperAdmin = user?.isSuperAdmin || !user?.subRole;
  const staffPermissions =
    (logedInUser?.subRole &&
      logedInUser?.subRole?.permissions &&
      logedInUser?.subRole?.permissions[0]) ||
    (user?.subRole && user?.subRole?.permissions && user?.subRole?.permissions[0]) ||
    {};

  const hasStaffPermission = (key) => {
    if (isSuperAdmin) return true;
    if (!key) return true;
    return Boolean(staffPermissions[key]);
  };

  const canView = true;
  const canChangeStatus =
    isSuperAdmin ||
    hasPermission(user, "contact_us", "reply") ||
    hasStaffPermission("contactUs_edit") ||
    hasStaffPermission("contactUs");
  const canReply =
    isSuperAdmin ||
    hasPermission(user, "contact_us", "reply") ||
    hasStaffPermission("contactUs_reply") ||
    hasStaffPermission("contactUs_edit") ||
    hasStaffPermission("contactUs");
  const canDelete =
    isSuperAdmin ||
    hasPermission(user, "contact_us", "delete") ||
    hasStaffPermission("contactUs_delete");

  // State
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalCount, setTotalCount] = useState(0);

  // Filters & Pagination (25 per page by default)
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);
  const [statusFilter, setStatusFilter] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  // Quick Reply Modal
  const [replyModalOpen, setReplyModalOpen] = useState(false);
  const [selectedInquiry, setSelectedInquiry] = useState(null);
  const [replySubject, setReplySubject] = useState("");
  const [replyMessage, setReplyMessage] = useState("");
  const [submittingReply, setSubmittingReply] = useState(false);

  // Handle search input changes (immediate clear on empty / backspace)
  const handleSearchChange = (val) => {
    setSearchQuery(val);
    if (!val || !val.trim()) {
      loader(true);
      setDebouncedSearch("");
      setPage(1);
    }
  };

  // Debounce search query input (350ms)
  useEffect(() => {
    if (!searchQuery.trim()) {
      setDebouncedSearch("");
      return;
    }
    const handler = setTimeout(() => {
      loader(true);
      setDebouncedSearch(searchQuery.trim());
      setPage(1); // Reset to page 1 on new search
    }, 350);
    return () => clearTimeout(handler);
  }, [searchQuery]);

  // Fetch paginated inquiries from API (100% dynamic from backend)
  const fetchInquiries = useCallback(
    async (showLoader = true) => {
      if (showLoader) loader(true);
      setLoading(true);

      try {
        const queryParams = new URLSearchParams();
        queryParams.append("page", String(page));
        queryParams.append("limit", String(limit));
        
        // Only append status when filtering by a specific status (never send status=all)
        if (statusFilter && statusFilter !== "all") {
          queryParams.append("status", statusFilter);
        }
        
        // Only append search if search term exists
        if (debouncedSearch && debouncedSearch.trim()) {
          queryParams.append("search", debouncedSearch.trim());
        }

        const endpoint = `${MODULE_CONFIG.endpoints.listing}?${queryParams.toString()}`;
        const res = await ApiClient.get(endpoint);

        if (res?.success) {
          const data = res?.data;
          const records = Array.isArray(data)
            ? data
            : Array.isArray(data?.data)
            ? data.data
            : [];
          setItems(records);

          // API returns total count at res.total or res.totalDocs
          const dynamicTotal = res?.total ?? res?.totalDocs ?? data?.total ?? records.length;
          setTotalCount(Number(dynamicTotal) || 0);
        }
      } catch (err) {
        console.error("Failed to fetch contact inquiries:", err);
      } finally {
        setLoading(false);
        if (showLoader) loader(false);
      }
    },
    [page, limit, statusFilter, debouncedSearch]
  );

  useEffect(() => {
    fetchInquiries(true);
  }, [fetchInquiries]);

  // Reset all active filters
  const isFilterActive = Boolean(searchQuery.trim() || (statusFilter && statusFilter !== "all"));

  const handleResetFilters = () => {
    loader(true);
    setSearchQuery("");
    setDebouncedSearch("");
    setStatusFilter("all");
    setPage(1);
  };

  // Handle Status Toggle (Read / Unread only)
  const handleToggleStatus = async (item) => {
    const id = item?.id || item?._id;
    const nextStatus = item.status === "read" ? "unread" : "read";
    loader(true);
    try {
      const res = await ApiClient.put(
        `${MODULE_CONFIG.endpoints.statusChange}?id=${id}&status=${nextStatus}`
      );
      if (res?.success) {
        toast.success(`Marked as ${capitalize(nextStatus)}.`);
        fetchInquiries(true);
      }
    } catch (err) {
      console.error("Failed to change status:", err);
    } finally {
      loader(false);
    }
  };

  // Open Quick Reply Modal
  const openReplyModal = (item) => {
    setSelectedInquiry(item);
    const customerDisplayName =
      item.fullName?.trim() ||
      item.name?.trim() ||
      (item.email ? item.email.split("@")[0] : "Customer");
    setReplySubject(`Re: Inquiry from ${capitalize(customerDisplayName)}`);
    setReplyMessage("");
    setReplyModalOpen(true);
  };

  // Submit Quick Reply
  const handleSendReply = async (e) => {
    e.preventDefault();
    if (!replyMessage.trim()) {
      toast.error("Please enter a reply message.");
      return;
    }
    const id = selectedInquiry?.id || selectedInquiry?._id;
    setSubmittingReply(true);
    loader(true);

    try {
      const payload = {
        contactUsIds: [id],
        subject: replySubject.trim(),
        message: replyMessage.trim(),
      };
      const res = await ApiClient.post(MODULE_CONFIG.endpoints.reply, payload);
      if (res?.success) {
        toast.success("Reply sent successfully to customer.");
        setReplyModalOpen(false);
        fetchInquiries(true);
      }
    } catch (err) {
      console.error("Failed to send reply:", err);
    } finally {
      setSubmittingReply(false);
      loader(false);
    }
  };

  // Handle Delete with confirmation
  const handleDelete = (item) => {
    const id = item?.id || item?._id;
    const customerDisplayName =
      item.fullName?.trim() ||
      item.name?.trim() ||
      (item.email ? item.email.split("@")[0] : "Customer");

    swal({
      title: "Delete Inquiry?",
      text: `Are you sure you want to delete inquiry from ${capitalize(customerDisplayName)}?`,
      icon: "warning",
      buttons: ["Cancel", "Delete"],
      dangerMode: true,
      className: "smallmodal",
    }).then(async (confirmed) => {
      if (!confirmed) return;
      loader(true);
      try {
        const res = await ApiClient.delete(
          `${MODULE_CONFIG.endpoints.delete}?id=${id}`
        );
        if (res?.success) {
          toast.success("Inquiry deleted successfully.");
          fetchInquiries();
        }
      } catch (err) {
        console.error("Failed to delete inquiry:", err);
      } finally {
        loader(false);
      }
    });
  };

  return (
    <Layout>
      <div className="w-full max-w-full space-y-5 pb-12 antialiased">
        
        {/* ======================================================== */}
        {/* 1. COMBINED MODULE TITLE & FILTER BAR (SPACE OPTIMIZED) */}
        {/* ======================================================== */}
        <div className="bg-white rounded-2xl p-4 sm:p-5 border border-slate-200/90 shadow-[0_2px_10px_rgba(0,0,0,0.03)] flex flex-col lg:flex-row lg:items-center justify-between gap-4">
          <div className="space-y-0.5 min-w-0">
            <h1 className="text-xl sm:text-2xl font-black text-slate-900 tracking-tight truncate">
              {MODULE_CONFIG.title}
            </h1>
            <p className="text-xs text-slate-600 font-medium">
              {MODULE_CONFIG.subtitle}
            </p>
          </div>

          {/* Filter & Search Controls */}
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2.5 flex-wrap shrink-0">
            {/* Search Input */}
            <div className="w-full sm:w-64 md:w-72">
              <div className="relative flex items-center group">
                <span className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-slate-400 group-focus-within:text-[#115B9D] transition-colors">
                  <FiSearch className="w-4 h-4" />
                </span>
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => handleSearchChange(e.target.value)}
                  placeholder="Search customer, email..."
                  className="w-full bg-white border-[1.5px] border-slate-500 rounded-xl pl-10 pr-9 h-10 text-xs sm:text-sm font-semibold text-slate-900 placeholder:text-slate-500 outline-none focus:outline-none focus:ring-0 focus:border-slate-500 shadow-box"
                />
                {searchQuery && (
                  <button
                    type="button"
                    onClick={() => handleSearchChange("")}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center text-slate-400 hover:text-slate-600 transition-colors cursor-pointer"
                    title="Clear search"
                  >
                    <FiX className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>

            {/* Status Filter Dropdown */}
            <div className="w-full sm:w-44">
              <FormControl
                type="select"
                placeholder="Filter by Status"
                value={statusFilter}
                theme="search"
                options={[
                  { id: "all", name: "All Statuses" },
                  { id: "unread", name: "Unread" },
                  { id: "read", name: "Read" },
                  { id: "replied", name: "Replied" },
                ]}
                onChange={(val) => {
                  loader(true);
                  setStatusFilter(val || "all");
                  setPage(1);
                }}
              />
            </div>

            {/* Reset Filters Button */}
            <button
              type="button"
              onClick={handleResetFilters}
              disabled={!isFilterActive}
              className={`inline-flex items-center justify-center gap-1.5 px-3.5 h-10 rounded-xl text-xs font-bold transition-all whitespace-nowrap cursor-pointer ${
                isFilterActive
                  ? "bg-white hover:bg-slate-100 active:bg-slate-200 text-slate-700 hover:text-slate-950 border border-slate-300 hover:border-slate-400 shadow-xs"
                  : "bg-slate-50 text-slate-400 border border-slate-200 opacity-50 cursor-not-allowed"
              }`}
              title="Reset all search and status filters"
            >
              <FiRotateCcw className={`w-3.5 h-3.5 ${isFilterActive ? "text-slate-600" : "text-slate-400"}`} />
              <span>Reset</span>
            </button>
          </div>
        </div>

        {/* ======================================================== */}
        {/* 2. INQUIRIES LISTING CONTAINER (SEAMLESS CARD VIEW ON MOBILE) */}
        {/* ======================================================== */}
        <div className="bg-transparent md:bg-white md:rounded-2xl md:border md:border-slate-200 md:shadow-[0_2px_10px_rgba(0,0,0,0.03)]">
          
          {/* Loading Indicator */}
          {loading && items.length === 0 ? (
            <div className="py-16 text-center bg-white rounded-2xl border border-slate-200 md:border-0">
              <div className="w-8 h-8 border-3 border-[#115B9D] border-t-transparent rounded-full animate-spin mx-auto mb-3" />
              <p className="text-xs font-bold text-slate-800">Loading Inquiries...</p>
            </div>
          ) : items.length === 0 ? (
            <div className="py-16 text-center space-y-2 p-6">
              <div className="w-12 h-12 rounded-2xl bg-slate-100 border border-slate-200 text-slate-500 flex items-center justify-center mx-auto mb-2">
                <FiMail className="w-6 h-6" />
              </div>
              <h3 className="text-sm font-black text-slate-900">No Inquiries Found</h3>
              <p className="text-xs font-medium text-slate-600 max-w-sm mx-auto">
                {debouncedSearch || statusFilter !== "all"
                  ? "No contact submissions match your current filters. Try resetting search or status filters."
                  : "No customer contact requests have been submitted yet."}
              </p>
              {isFilterActive && (
                <div className="pt-2">
                  <button
                    type="button"
                    onClick={handleResetFilters}
                    className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-bold transition-all cursor-pointer"
                  >
                    <FiRotateCcw className="w-3.5 h-3.5" />
                    <span>Clear All Filters</span>
                  </button>
                </div>
              )}
            </div>
          ) : (
            <>
              {/* DESKTOP TABLE VIEW (FULL-WIDTH INSIDE SINGLE CARD) */}
              <div className="hidden md:block w-full">
                <table className="w-full table-fixed border-collapse text-left text-xs">
                  <colgroup>
                    <col style={{ width: "7%" }} />   {/* 1. Action (1st column) */}
                    <col style={{ width: "20%" }} />  {/* 2. Customer */}
                    <col style={{ width: "16%" }} />  {/* 3. Organization / Tenant */}
                    <col style={{ width: "18%" }} />  {/* 4. Contact Info */}
                    <col style={{ width: "23%" }} />  {/* 5. Message */}
                    <col style={{ width: "16%" }} />  {/* 6. Status (Fully visible) */}
                  </colgroup>

                  <thead className="sticky top-[72px] z-30 bg-slate-50 shadow-xs">
                    <tr className="border-b border-slate-200 text-slate-600 font-bold uppercase tracking-wider text-[11px] bg-slate-50">
                      <th className="sticky top-[72px] z-30 bg-slate-50 py-3.5 px-3 text-center whitespace-nowrap border-b border-slate-200 shadow-xs rounded-tl-2xl">Action</th>
                      <th className="sticky top-[72px] z-30 bg-slate-50 py-3.5 px-4 text-left border-b border-slate-200 shadow-xs">Customer</th>
                      <th className="sticky top-[72px] z-30 bg-slate-50 py-3.5 px-4 text-left border-b border-slate-200 shadow-xs">Tenant / Org</th>
                      <th className="sticky top-[72px] z-30 bg-slate-50 py-3.5 px-4 text-left border-b border-slate-200 shadow-xs">Contact</th>
                      <th className="sticky top-[72px] z-30 bg-slate-50 py-3.5 px-4 text-left border-b border-slate-200 shadow-xs">Message</th>
                      <th className="sticky top-[72px] z-30 bg-slate-50 py-3.5 px-4 text-left whitespace-nowrap border-b border-slate-200 shadow-xs rounded-tr-2xl">Status</th>
                    </tr>
                  </thead>

                  <tbody className="divide-y divide-slate-100">
                    {items.map((item, index) => {
                      const id = item.id || item._id;
                      const customerDisplayName =
                        item.fullName?.trim() ||
                        item.name?.trim() ||
                        (item.email ? item.email.split("@")[0] : "Customer");
                      const customerName = capitalize(customerDisplayName);
                      const tenantOrg = capitalize(
                        item.tenantName ||
                        item.organization ||
                        item.companyName ||
                        item.tenantId?.name ||
                        "Direct Platform"
                      );
                      const isUnread = item.status === "unread";
                      const badgeInfo =
                        MODULE_CONFIG.statusMap[item.status] || MODULE_CONFIG.statusMap.unread;
                      const isLastRow = index === items.length - 1;
                      const hasPagination = totalCount > limit;

                      return (
                        <tr
                          key={id}
                          onClick={() => navigate(`${MODULE_CONFIG.routeBase}/detail/${id}`)}
                          className={`cursor-pointer transition-colors border-b border-slate-100 last:border-0 group ${
                            isUnread
                              ? "bg-amber-50/25 hover:bg-amber-50/60"
                              : "hover:bg-slate-50/90"
                          }`}
                        >
                          {/* 1. Reusable 3-Dots Action Menu (FIRST COLUMN) */}
                          <td
                            className={`py-3.5 px-3 align-middle text-center ${
                              isLastRow && !hasPagination ? "rounded-bl-2xl" : ""
                            }`}
                            onClick={(e) => e.stopPropagation()}
                          >
                            <div className="flex items-center justify-center">
                              <ActionMenu
                                align="left"
                                direction="auto"
                                actions={[
                                  {
                                    label: "View Detail",
                                    icon: <FiEye />,
                                    hidden: !canView,
                                    onClick: () =>
                                      navigate(`${MODULE_CONFIG.routeBase}/detail/${id}`),
                                  },
                                  {
                                    label: isUnread ? "Mark as Read" : "Mark as Unread",
                                    icon: isUnread ? <FiCheckCircle /> : <FiClock />,
                                    hidden: !canChangeStatus,
                                    onClick: () => handleToggleStatus(item),
                                  },
                                  {
                                    label: "Send Reply",
                                    icon: <FiCornerUpLeft />,
                                    hidden: !canReply,
                                    onClick: () => openReplyModal(item),
                                  },
                                  {
                                    label: "Delete",
                                    icon: <FiTrash2 />,
                                    isDanger: true,
                                    hidden: !canDelete,
                                    onClick: () => handleDelete(item),
                                  },
                                ]}
                              />
                            </div>
                          </td>

                          {/* 2. Customer Name & Initials */}
                          <td className="py-3.5 px-4 align-middle overflow-hidden">
                            <div className="flex items-center gap-2.5 min-w-0">
                              <div className="w-8 h-8 rounded-xl bg-gradient-to-br from-blue-50 to-indigo-100 border border-blue-200/80 text-[#115B9D] font-black text-xs flex items-center justify-center shrink-0 shadow-xs">
                                {getInitials(customerName)}
                              </div>
                              <div className="min-w-0">
                                <div className={`font-bold text-xs truncate group-hover:text-[#115B9D] transition-colors ${
                                  isUnread ? "text-slate-950 font-black" : "text-slate-900"
                                }`}>
                                  {customerName}
                                </div>
                                <div className="text-[10px] text-slate-500 font-medium">
                                  {formatDate(item.createdAt)}
                                </div>
                              </div>
                            </div>
                          </td>

                          {/* 3. Tenant Organization */}
                          <td className="py-3.5 px-4 align-middle overflow-hidden">
                            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-[11px] font-bold text-slate-800 bg-slate-100 border border-slate-200/90 truncate max-w-full">
                              <HiOutlineBuildingOffice2 className="w-3.5 h-3.5 text-[#115B9D] shrink-0" />
                              <span className="truncate">{tenantOrg}</span>
                            </span>
                          </td>

                          {/* 4. Contact Info (Email & Phone) */}
                          <td className="py-3.5 px-4 align-middle overflow-hidden">
                            <div className="min-w-0">
                              <div className="font-semibold text-slate-800 truncate" title={item.email}>
                                {item.email || "-"}
                              </div>
                              {item.phone || item.mobile ? (
                                <div className="text-[10px] text-slate-500 font-medium truncate">
                                  {item.phone || item.mobile}
                                </div>
                              ) : null}
                            </div>
                          </td>

                          {/* 5. Message Snippet */}
                          <td className="py-3.5 px-4 align-middle overflow-hidden">
                            <p
                              className="text-xs text-slate-600 group-hover:text-slate-900 transition-colors leading-relaxed line-clamp-2"
                              title={item.message || item.description}
                            >
                              {capitalizeSentence(item.message || item.description || "No message content")}
                            </p>
                          </td>

                          {/* 6. Status Badge (Generous Space, Fully Visible) */}
                          <td
                            className={`py-3.5 px-4 align-middle ${
                              isLastRow && !hasPagination ? "rounded-br-2xl" : ""
                            }`}
                          >
                            <span
                              className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-wide border whitespace-nowrap ${badgeInfo.bgClass}`}
                            >
                              <span className={`w-1.5 h-1.5 rounded-full ${badgeInfo.dotClass}`} />
                              {badgeInfo.label}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* MOBILE CARD VIEW (Full Card Click Navigation) */}
              <div className="block md:hidden space-y-3">
                {items.map((item) => {
                  const id = item.id || item._id;
                  const customerDisplayName =
                    item.fullName?.trim() ||
                    item.name?.trim() ||
                    (item.email ? item.email.split("@")[0] : "Customer");
                  const customerName = capitalize(customerDisplayName);
                  const tenantOrg = capitalize(
                    item.tenantName ||
                    item.organization ||
                    item.companyName ||
                    item.tenantId?.name ||
                    "Direct Platform"
                  );
                  const isUnread = item.status === "unread";
                  const badgeInfo =
                    MODULE_CONFIG.statusMap[item.status] || MODULE_CONFIG.statusMap.unread;

                  return (
                    <div
                      key={id}
                      onClick={() => navigate(`${MODULE_CONFIG.routeBase}/detail/${id}`)}
                      className={`p-4 rounded-2xl border transition-all cursor-pointer shadow-xs space-y-3 ${
                        isUnread
                          ? "bg-amber-50/25 border-amber-200/70 hover:border-amber-300 hover:shadow-md"
                          : "bg-white border-slate-200 hover:border-blue-300 hover:shadow-md"
                      }`}
                    >
                      {/* Top Header: Customer + 3 Dots */}
                      <div className="flex items-center justify-between gap-2">
                        <div className="flex items-center gap-2.5 min-w-0">
                          <div className="w-8 h-8 rounded-xl bg-gradient-to-br from-blue-50 to-indigo-100 border border-blue-200/80 text-[#115B9D] font-black text-xs flex items-center justify-center shrink-0">
                            {getInitials(customerName)}
                          </div>
                          <div className="min-w-0">
                            <h4 className="font-extrabold text-xs text-slate-900 truncate">
                              {customerName}
                            </h4>
                            <p className="text-[11px] text-slate-500 font-medium truncate">
                              {item.email}
                            </p>
                          </div>
                        </div>

                        {/* Stop propagation so 3-dots doesn't navigate to detail */}
                        <div onClick={(e) => e.stopPropagation()}>
                          <ActionMenu
                            align="left"
                            actions={[
                              {
                                label: "View Detail",
                                icon: <FiEye />,
                                hidden: !canView,
                                onClick: () =>
                                  navigate(`${MODULE_CONFIG.routeBase}/detail/${id}`),
                              },
                              {
                                label: isUnread ? "Mark as Read" : "Mark as Unread",
                                icon: isUnread ? <FiCheckCircle /> : <FiClock />,
                                hidden: !canChangeStatus,
                                onClick: () => handleToggleStatus(item),
                              },
                              {
                                label: "Send Reply",
                                icon: <FiCornerUpLeft />,
                                hidden: !canReply,
                                onClick: () => openReplyModal(item),
                              },
                              {
                                label: "Delete",
                                icon: <FiTrash2 />,
                                isDanger: true,
                                hidden: !canDelete,
                                onClick: () => handleDelete(item),
                              },
                            ]}
                          />
                        </div>
                      </div>

                      {/* Tenant Organization */}
                      <div className="flex items-center justify-between text-xs pt-1 border-t border-slate-100">
                        <span className="text-[11px] text-slate-500 font-semibold">Tenant Org:</span>
                        <span className="font-bold text-xs text-slate-800 bg-slate-50 px-2.5 py-0.5 rounded-lg border border-slate-200 truncate max-w-[200px]">
                          {tenantOrg}
                        </span>
                      </div>

                      {/* Message preview */}
                      <p className="text-xs text-slate-700 font-medium line-clamp-2 bg-slate-50/70 p-2.5 rounded-xl border border-slate-200/80">
                        {capitalizeSentence(item.message || item.description || "No message content")}
                      </p>

                      {/* Footer: Status + Date */}
                      <div className="flex items-center justify-between pt-1 text-xs">
                        <span
                          className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-black uppercase tracking-wide border ${badgeInfo.bgClass}`}
                        >
                          <span className={`w-1.5 h-1.5 rounded-full ${badgeInfo.dotClass}`} />
                          {badgeInfo.label}
                        </span>

                        <span className="text-[11px] font-semibold text-slate-500">
                          {formatDate(item.createdAt)} {formatTime(item.createdAt)}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* COMMON REUSABLE PAGINATION (Rendered ONLY when totalCount > limit) */}
              {totalCount > limit && (
                <div className="p-4 bg-white border-t border-slate-100 rounded-b-2xl">
                  <CommonPagination
                    page={page}
                    limit={limit}
                    total={totalCount}
                    onPageChange={(newPage) => {
                      loader(true);
                      setPage(newPage);
                    }}
                    onLimitChange={(newLimit) => {
                      loader(true);
                      setLimit(newLimit);
                      setPage(1);
                    }}
                  />
                </div>
              )}
            </>
          )}

        </div>

      </div>

      {/* ======================================================== */}
      {/* 4. EXECUTIVE QUICK REPLY MODAL */}
      {/* ======================================================== */}
      <Transition appear show={replyModalOpen} as={Fragment}>
        <Dialog
          as="div"
          className="relative z-[99999]"
          onClose={() => setReplyModalOpen(false)}
        >
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-200"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-150"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-slate-950/60 backdrop-blur-md z-[99999]" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto z-[100000]">
            <div className="flex min-h-full items-center justify-center p-4">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-200"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-150"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="w-full max-w-lg transform overflow-hidden rounded-2xl bg-white shadow-2xl transition-all border border-slate-200">
                  {/* Clean Modal Header */}
                  <div className="px-6 py-4 border-b border-slate-100 bg-slate-50/70 flex items-center justify-between">
                    <div className="flex items-center gap-2.5">
                      <div className="w-8 h-8 rounded-lg bg-blue-50 border border-blue-200 text-[#115B9D] flex items-center justify-center">
                        <FiSend className="w-4 h-4" />
                      </div>
                      <div>
                        <Dialog.Title className="text-sm sm:text-base font-black text-slate-900">
                          Reply to Customer Inquiry
                        </Dialog.Title>
                        <p className="text-[11px] font-medium text-slate-500">
                          Direct email reply to the customer
                        </p>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => setReplyModalOpen(false)}
                      className="p-1.5 rounded-lg text-slate-400 hover:text-slate-700 hover:bg-slate-200/60 transition-colors cursor-pointer"
                    >
                      <FiX className="w-4 h-4" />
                    </button>
                  </div>

                  {/* Modal Body */}
                  <form onSubmit={handleSendReply} className="p-6 space-y-4 text-xs">
                    {/* Customer Context Dossier */}
                    <div className="p-3.5 rounded-xl bg-slate-50 border border-slate-200 space-y-2">
                      <div className="flex items-center justify-between flex-wrap gap-2">
                        <div className="flex items-center gap-2">
                          <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500">
                            Recipient:
                          </span>
                          <span className="font-extrabold text-slate-900">
                            {capitalize(
                              selectedInquiry?.fullName?.trim() ||
                              selectedInquiry?.name?.trim() ||
                              (selectedInquiry?.email ? selectedInquiry?.email.split("@")[0] : "Customer")
                            )}
                          </span>
                        </div>
                        <span className="font-semibold text-slate-600 bg-white px-2 py-0.5 rounded border border-slate-200 text-[11px]">
                          {selectedInquiry?.email || "-"}
                        </span>
                      </div>
                      <div className="text-[11px] text-slate-600 font-medium bg-white p-2.5 rounded-lg border border-slate-200/80 italic line-clamp-2">
                        "{selectedInquiry?.message || selectedInquiry?.description || "No message content"}"
                      </div>
                    </div>

                    {/* Subject Line with platform FormControl */}
                    <div>
                      <FormControl
                        type="text"
                        label="Subject Line"
                        required
                        value={replySubject}
                        onChange={(val) => setReplySubject(val)}
                        placeholder="Enter subject..."
                      />
                    </div>

                    {/* Reply Content with platform FormControl */}
                    <div>
                      <FormControl
                        type="textarea"
                        label="Reply Message"
                        required
                        value={replyMessage}
                        onChange={(val) => setReplyMessage(val)}
                        placeholder="Type your official response to this inquiry..."
                      />
                    </div>

                    {/* Modal Footer Bar */}
                    <div className="pt-3 border-t border-slate-100 flex items-center justify-end gap-2.5">
                      <button
                        type="button"
                        onClick={() => setReplyModalOpen(false)}
                        className="px-4 py-2.5 rounded-xl border border-slate-200 text-slate-700 hover:bg-slate-100 font-bold transition-all cursor-pointer"
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        disabled={submittingReply}
                        className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-[#115B9D] hover:bg-[#0E528F] active:bg-[#0A3E6E] text-white font-bold transition-all shadow-md shadow-[#115B9D]/25 disabled:opacity-60 cursor-pointer"
                      >
                        <FiSend className={`w-3.5 h-3.5 ${submittingReply ? "animate-spin" : ""}`} />
                        <span>{submittingReply ? "Dispatching..." : "Send Reply"}</span>
                      </button>
                    </div>
                  </form>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
    </Layout>
  );
}
